import { Logo } from "@repo/ui/components/ui/header/logo";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@repo/ui/components/ui/sheet";
import { ModeToggle } from "@repo/ui/components/ui/mode-toogle";
import Icon from "@repo/ui/components/ui/icon/icon";
import { Secondary } from "@repo/ui/components/ui/header/secondary";
import { cn } from "@repo/ui/lib/utils";
import {
  ComponentType,
  Dispatch,
  ElementType,
  SetStateAction,
  useState,
} from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverClose,
} from "@repo/ui/components/ui/popover";

export type HeaderItemProps = {
  text: string;
  isPrimary?: boolean;
  href: string;
  selected?: boolean;
  items?: Array<HeaderItemProps>;
};

export type Category = {
  id: string;
  productCode: string;
  title: string;
  imgUrl: string;
  showOnHome: boolean;
  children?: Array<Category>;
  isBrand?: boolean;
  brands?: Array<string>;
};

export type HeaderProps = {
  primaryItems?: Array<HeaderItemProps>;
  secondaryItems?: Array<Category>;
  navItems?: Array<React.ReactNode>;
  children?: {
    open: React.ReactNode;
    closed: React.ReactNode;
    languageSwitcher?: React.ReactNode;
    logout?: React.ReactNode;
  };
  LinkCmp: ComponentType | ElementType;
  logoHref?: string;
  openSheet?: boolean;
  setOpenSheet?: Dispatch<SetStateAction<boolean>>;
  primaryMenuHiddenOnDesktop?: boolean;
  brandsLabel?: string;
};

const Header: React.FC<HeaderProps> = ({
  primaryItems,
  secondaryItems,
  LinkCmp,
  logoHref = "/",
  primaryMenuHiddenOnDesktop = false,
  children,
  brandsLabel,
}) => {
  const [openSheet, setOpenSheet] = useState(false);
  const navItems = primaryItems?.map((item, index) => (
    <div
      key={index}
      className={cn("relative", { "md:hidden": primaryMenuHiddenOnDesktop })}
    >
      {item.items ? (
        <Popover>
          <PopoverTrigger asChild>
            <div
              className={cn(
                "nav-item cursor-pointer md:flex md:items-center",
                { "nav-item-primary": item.isPrimary },
                {
                  "font-black":
                    item.selected ||
                    item.items.some((subItem) => subItem.selected),
                },
              )}
            >
              {item.text}
              <Icon
                className={cn(
                  "h-[17px] w-[17px] stroke-primary-black dark:stroke-primary-white",
                  "md:block md:h-[10px] md:w-[10px] md:ml-2 md:dark:stroke-primary-magenta",
                )}
                icon="ArrowDown"
              />
            </div>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="rounded-[10px] rounded-tl-none"
          >
            {item.items.map((subItem, subIndex) => (
              <PopoverClose asChild key={subIndex}>
                <LinkCmp
                  href={subItem.href}
                  className={cn(
                    "block mx-2 px-2 py-2 text-normal text-primary-black border-b border-secondary-gray1 last:border-none",
                    {
                      "font-black": subItem.selected,
                    },
                  )}
                >
                  {subItem.text}
                </LinkCmp>
              </PopoverClose>
            ))}
          </PopoverContent>
        </Popover>
      ) : (
        <LinkCmp
          key={index}
          href={item.href}
          onClick={() => setOpenSheet(false)}
          className={cn(
            "nav-item",
            { "nav-item-primary": item.isPrimary },
            { "font-black": item.selected },
          )}
        >
          {item.text}
          <Icon
            className="hidden max-md:block h-[17px] w-[17px] stroke-primary-black dark:stroke-primary-white"
            icon="Arrow"
          />
        </LinkCmp>
      )}
    </div>
  ));

  return (
    <>
      <SheetMenu
        openSheet={openSheet}
        setOpenSheet={setOpenSheet}
        navItems={navItems}
        secondaryItems={secondaryItems}
        LinkCmp={LinkCmp}
        children={children}
        brandsLabel={brandsLabel}
      />

      <header className="z-50 drop-shadow-header dark:drop-shadow-header-dark sticky top-0 max-md:h-[var(--mobile-header-height)] max-md:px-[28px] w-full bg-primary-white dark:bg-black text-white">
        <nav className="flex flex-wrap justify-start max-md:justify-between py-7 max-md:py-[24px] px-10 max-md:px-0 gap-10 items-center text-base">
          <Icon
            onClick={() => setOpenSheet(true)}
            icon="Hamburger"
            className="md:hidden h-[24px] w-[24px]"
          />
          <LinkCmp href={logoHref}>
            <Logo className="max-md:absolute max-md:left-[50%] max-md:top-[50%] max-md:translate-x-[-50%] max-md:translate-y-[-50%]" />
          </LinkCmp>
          <div className="max-md:hidden">
            <div className="nav-items">{navItems}</div>
          </div>
          <div className="md:ml-auto">{children?.closed}</div>
        </nav>

        {secondaryItems && (
          <div className="max-md:hidden">
            <Secondary
              items={secondaryItems}
              LinkCmp={LinkCmp}
              brandsLabel={brandsLabel}
            />
          </div>
        )}
      </header>
      <div className="z-50 fixed bottom-[40px] left-[40px] max-md:hidden">
        <ModeToggle />
      </div>
    </>
  );
};

const SheetMenu = ({
  navItems,
  secondaryItems,
  LinkCmp,
  openSheet,
  setOpenSheet,
  children,
  brandsLabel,
}: HeaderProps) => {
  return (
    <Sheet open={openSheet} onOpenChange={setOpenSheet}>
      <SheetContent
        onOpenAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        side="left"
        className="w-full dark:bg-secondary-black max-h-screen overflow-y-scroll overflow-x-hidden"
      >
        <SheetTitle className="hidden">Menu</SheetTitle>
        <SheetDescription className="hidden">Menu</SheetDescription>
        <header className="drop-shadow-header dark:drop-shadow-header-dark max-md:px-[24px] max-md:px-[28px] w-full bg-primary-white dark:bg-black text-white">
          <nav className="flex flex-wrap justify-start max-md:justify-between py-7 max-md:py-[24px] px-10 max-md:px-0 gap-10 items-center text-base">
            <SheetClose>
              <Icon icon="Close" className="h-[24px] w-[24px]" />
            </SheetClose>
            <Logo />

            <>
              <div></div>
              <SheetClose asChild>{children?.open}</SheetClose>
            </>
          </nav>
        </header>
        <div className="p-[24px] pb-[100px] mt-[20px]">
          <ModeToggle></ModeToggle>
          {children?.languageSwitcher && (
            <div className="mt-[40px]">{children?.languageSwitcher}</div>
          )}
          {secondaryItems && setOpenSheet && (
            <Secondary
              closeAction={() => setOpenSheet(false)}
              items={secondaryItems}
              LinkCmp={LinkCmp}
              brandsLabel={brandsLabel}
            />
          )}
          <div className="nav-items mt-[40px]">{navItems}</div>
          {children?.logout && (
            <SheetClose asChild>
              <div className="mt-[40px]">{children?.logout}</div>
            </SheetClose>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default Header;
